import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { Observable, forkJoin, from, map, switchMap, take } from 'rxjs';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { ConvertReplaceService } from '../../services/convert-replace.service';
import { Rating } from '../../services/ratings.service';
import { StoryblokReplaceService } from '../../services/storyblok-replace.service';
import { convertStoryblokCustomRatingsResolver } from '../../storyblok/convert-storyblok-custom-ratings.resolver';
import { convertFormatsResolver } from './convert-formats.resolver';
import { convertStoryblokRelationshipsResolver } from './convert-relationships.resolver';
import { getFormatsFromFormatParam } from './get-formats-from-format-param';

export const convertStoryblokStoryResolver: ResolveFn<Observable<ISbStoryData>> = (route, state) => {
  const storyblokService = inject(PICFLOW_STORYBLOK);
  const storyblokReplaceService = inject(StoryblokReplaceService);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const convertReplaceService = inject(ConvertReplaceService); // just loading this to make sure the service is active

  const { from: fromFormatSlug, to: toFormatSlug } = getFormatsFromFormatParam(route.params.format);
  const path = toFormatSlug ? 'convert/conversion-template' : 'convert/format-template';
  return forkJoin([
    storyblokService.getStory(path, route).pipe(
      map(story => story && story.story),
      switchMap(story => from(storyblokService.loadComponents(story)).pipe(map(() => story)))
    ),
    convertFormatsResolver(route, state) as Observable<ISbStoryData[]>,
    convertStoryblokRelationshipsResolver(route, state) as Observable<ISbStoryData[]>,
    (convertStoryblokCustomRatingsResolver(route, state) as Observable<Rating[]>).pipe(take(1)),
  ]).pipe(
    map(([story, formats, relationships, customRatings]) => {
      const fromFormat = formats.find(format => format.slug === fromFormatSlug);
      const toFormat = formats.find(format => format.slug === toFormatSlug);

      if (!fromFormat) {
        console.warn('Cant find from format.');
        return null;
      }

      if (toFormatSlug) {
        if (!toFormat) {
          console.warn('Cant find to format.');
          return null;
        }
        if (toFormat.content.sourceOnly) {
          console.warn('Cant convert to a source only format.');
          return null;
        }
      }

      if (!story) {
        console.warn('Cant find story.');
        return null;
      }

      return storyblokReplaceService.replaceStoryValues(story, route, {
        formats,
        relationships,
        customRatings,
      });
    })
  );
};
